import { ICorporationResponse } from '@kcftech/prometheustypescriptapiclient';
import {
    Dropdown,
    DropdownItemRenderer,
    DropdownTriggerOptions,
} from 'components/dropdown/Dropdown';
import {
    onDropdownItemLinkClick,
    SimpleDropdownLabelRenderer,
    useDropdownLabelRenderer,
} from 'components/dropdown/shared';
import { DropdownItemLink } from 'components/dropdown/styled';
import { useScopeNavigation } from 'components/top_bar/useScopeNavigation';
import { useUser } from 'contexts/UserContext';
import {
    infiniteCorporationsDataSourceKey,
    useInfiniteCorporationDataSource,
} from 'custom_hooks/data_hooks/data_source/corporationInfiniteDataSource';
import { useCallback } from 'react';
import { getId } from 'utils/data';

export type CorporationDropdownProps = Readonly<{
    /**
     * The currently selected corporation.
     */
    value: ICorporationResponse | undefined;
    /**
     * Callback to call when the selected corporation should change.
     */
    onChange?: (corporation: ICorporationResponse) => void;
    /**
     * Placeholder text for the search field.
     */
    placeholder?: string;
    className?: string;
    popupWidth?: string;
    renderLabel?: SimpleDropdownLabelRenderer<ICorporationResponse>;
    interactionMode?: 'link' | 'select';
    /**
     * Additional trigger configuration.
     */
    trigger?: DropdownTriggerOptions;
}>;

export const CorporationAbbreviation: SimpleDropdownLabelRenderer<ICorporationResponse> = ({
    value: selectedCorp,
}) => selectedCorp.Abbreviation ?? selectedCorp.Name.substring(0, 5);

const renderItemNoLink: DropdownItemRenderer<ICorporationResponse> = ({ item }) => (
    <div>{item.Name}</div>
);

/**
 * Dropdown which allows selecting a corporation from a list of available locations.
 */
export function CorporationDropdown({
    value,
    onChange,
    placeholder = 'Select a Corporation',
    className,
    popupWidth,
    renderLabel: renderLabelProp,
    interactionMode = 'link',
    trigger,
}: CorporationDropdownProps) {
    const { user } = useUser();
    const { determineCorporationRoute, navigateToCorporationPage } = useScopeNavigation(
        user
    );

    const renderLabel = useDropdownLabelRenderer({
        defaultRenderer: (info) => info.value.Name,
        overrideRenderer: renderLabelProp,
        placeholder,
    });

    const renderItemLink = useCallback<DropdownItemRenderer<ICorporationResponse>>(
        ({ item }) => (
            <DropdownItemLink
                onClick={onDropdownItemLinkClick}
                to={determineCorporationRoute(item)}
            >
                {item.Name}
            </DropdownItemLink>
        ),
        [determineCorporationRoute]
    );

    const {
        isLoading,
        hasNext,
        loadNext,
        results,
        filter,
        setFilter,
    } = useInfiniteCorporationDataSource({
        name: infiniteCorporationsDataSourceKey,
        defaultFilter: {},
    });

    const setSearchText = useCallback(
        (searchText: string) => {
            setFilter((currentFilter) => ({
                ...currentFilter,
                nameSearch: searchText,
            }));
        },
        [setFilter]
    );

    return (
        <Dropdown<ICorporationResponse>
            data={{
                items: results,
                isLoading,
                hasMore: hasNext ?? false,
                onLoadMore: loadNext,
            }}
            dataKey={getId}
            renderLabel={renderLabel}
            renderItem={interactionMode === 'link' ? renderItemLink : renderItemNoLink}
            value={value}
            onChange={
                onChange ??
                (interactionMode === 'link' ? navigateToCorporationPage : () => {})
            }
            filterable={{
                filterText: filter.nameSearch ?? '',
                onChange: setSearchText,
                placeholder: 'Search Corporations',
            }}
            trigger={{ className, ...trigger }}
            popup={{ width: popupWidth }}
        />
    );
}
